<template>
    <div class="upload">
        <div class="upload-list" v-if="!hideList">
            <slot name="list" :files="files">
                <template v-for="(file, index) in files">
                    <div class="upload-file" :key="index">
                        <slot name="file" :index="index" :file="file">
                            <flex>
                                <icon name="paperclip" class="upload-file-icon"/>
                                <a :href="file[urlKey]" target="_blank" class="upload-file-label text-truncate">{{ file[nameKey] }}</a>
                                <btn theme="none" icon="delete" :title="__('ui/upload_delete')" class="upload-file-delete" @click="handleDelete(index)"/>
                            </flex>
                        </slot>
                    </div>
                </template>
            </slot>
        </div>

        <div class="upload-button" v-if="enableUpload">
            <slot name="button" :id="id">
                <label :class="`btn btn-${theme}`" :for="id">{{ label || __('ui/upload_button') }}</label>
            </slot>
        </div>
        <input :id="id" type="file" :accept="accept" class="d-none" ref="input" @change="handleInput">
        <slot name="progress" v-if="inProgress">
            <loading-indicator/>
        </slot>

        <div class="upload-hint" v-if="!hideHint && hint">
            <slot name="hint">
                {{ hint }}
            </slot>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            value: {type: [Array, Object], default: null},
            multiple: {type: Boolean, default: false},
            hideHint: {type: Boolean, default: false},
            hideList: {type: Boolean, default: false},
            hideProgress: {type: Boolean, default: false},
            label: {type: String, default: null},
            theme: {type: String, default: 'secondary'},
            uploadAction: {type: [String, Function], required: true},
            formats: {type: Array, default: null},
            maxSize: {type: [String, Number], default: null},
            validator: {type: Function, default: null},
            limit: {type: [String, Number], default: null},
            payload: {type: Object, default: () => ({})},
            nameKey: {type: String, default: 'name'},
            urlKey: {type: String, default: 'url'},
        },

        data() {
            return {
                inProgress: false,
            };
        },

        computed: {
            id() {
                return `upload_input_${this._uid}`;
            },

            files() {
                if (this.value === null) {
                    return [];
                }
                if (Array.isArray(this.value)) {
                    return this.value;
                }
                return [this.value];
            },

            enableUpload() {
                if (this.limit !== null && this.limit >= this.files.count) {
                    return false;
                }
                return !this.inProgress;
            },

            accept() {
                if (this.formats === null) {
                    return null;
                }
                return this.formats.join(',');
            },

            types() {
                if (this.formats === null) {
                    return ['all'];
                }

                let types = [];
                this.formats.forEach(format => {
                    let type = format.split('/')[0];
                    if (type && !types.includes(type)) {
                        types.push(type);
                    }
                });
                return types;
            },

            hint() {
                let hint = [];

                if (this.formats !== null) {
                    let formats = this.formats.map(format => this.__(`ui/upload_formats_${format}`)).join(', ');
                    hint.push(`${this.__('ui/upload_formats')}: ${formats}`);
                }

                if (this.maxSize !== null) {
                    hint.push(this.__('ui/upload_max_size', this.maxSize));
                }

                if (this.limit !== null) {
                    hint.push(this.__('ui/upload_limit', this.limit));
                }

                return hint.join(' - ');
            },
        },

        methods: {
            async handleInput() {
                let rawFile = this.$refs.input.files[0];

                if (!rawFile) {
                    return;
                }

                if (this.limit !== null && this.limit <= this.files.length) {
                    return this.handleError('limit');
                }

                if (this.maxSize !== null && this.maxSize < (rawFile.size / 1024 / 1024)) {
                    return this.handleError('max-size');
                }
                if (this.validator instanceof Function) {
                    let result = this.validator(rawFile);
                    if (typeof result === 'string') {
                        return this.handleError(result);
                    }
                }

                this.inProgress = true;
                let payload = {file: rawFile, ...this.payload};
                let data;
                if (this.uploadAction instanceof Function) {
                    data = await this.uploadAction(payload);
                } else {
                    try {
                        data = await this.$store.dispatch(this.uploadAction, payload);
                    } catch ({message}) {
                        this.handleError(message);
                        this.inProgress = false;
                        return;
                    }
                }
                if (data instanceof Object && data.$success !== false) {
                    this.handleUpload(data);
                } else {
                    this.handleError('upload');
                }
                this.inProgress = false;
            },

            handleError(type) {
                this.$emit('error', type);
                this.$refs.input.value = null;
            },

            handleUpload(data) {
                if (this.multiple) {
                    this.$emit('input', [...this.files, data]);
                } else {
                    this.$emit('input', data);
                }
            },

            handleDelete(index) {
                if (this.multiple) {
                    let value = [...this.files];
                    value.splice(index, 1);
                    this.$emit('input', value);
                } else {
                    this.$emit('input', null);
                }
            },
        },
    };
</script>